// pages/Kyohon.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { useLocation } from "react-router-dom";

import Technique from "./components/TechniqueCard";
import Dropdown from "./components/Dropdown";
import ScrollToTopButton from "./components/ScrollToTop";
import { shouldShowTutorial } from "./utils/shouldShowTutorial";
import PlatformFilter from "./components/PlatformFilter";

import { db } from "./firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useAuth } from "./AuthContext";

const DEFAULT_FILTERS = {
  authors: "All",
  platforms: ["YouTube", "Shibari Study", "Patreon"],
  searchQuery: "",
  sortDirection: "Descending",
  showStreams: false,
};

const getSkeletonCount = () => {
  if (typeof window === "undefined") {
    return 8;
  }

  // Keep rows visually complete based on the current viewport.
  const viewportWidth = window.innerWidth;
  if (viewportWidth < 768) {
    return 4;
  }
  if (viewportWidth < 1200) {
    return 6;
  }
  return 8;
};

const isStream = (technique) => /[-_]stream$/i.test(technique.url);

const extractVimeoId = (videoUrl = "") => {
  const match = String(videoUrl).match(/vimeo\.com\/(\d+)/i);
  return match ? match[1] : null;
};

const normalizePatreonItem = (item, index) => {
  const videoUrl = item.video_url || "";
  const pageUrl = item.page_url || "";
  const vimeoId = extractVimeoId(videoUrl);
  const seriesValues = Array.isArray(item.series)
    ? item.series
    : item.series
      ? [item.series]
      : [];
  const tags = Array.isArray(item.tags) ? item.tags : [];
  const mergedTags = [...new Set([...seriesValues, ...tags])];

  return {
    name: item.title || `Patreon item ${index + 1}`,
    author: item.author || "Hua Hua",
    platform: "Patreon",
    description: item.description || item.excerpt || "",
    url: pageUrl || videoUrl || `patreon-${index + 1}`,
    previewUrl: videoUrl || pageUrl || "",
    linkLabel: pageUrl ? "Patreon Post" : "Patreon",
    secondaryUrl: pageUrl && videoUrl ? videoUrl : "",
    secondaryLabel: "Watch Video",
    videoUrl,
    thumbnail: vimeoId ? `https://vumbnail.com/${vimeoId}.jpg` : null,
    date: item.date || "",
    tags: mergedTags,
    series: seriesValues,
  };
};

function Kyohon() {
  const location = useLocation();
  const user = useAuth();
  const isAdmin = user?.email === "iamozthorn@gmail.com";
  const [patreonAccess, setPatreonAccess] = useState(false);

  const [techniques, setTechniques] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [skeletonCount, setSkeletonCount] = useState(getSkeletonCount);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [filteredTechniques, setFilteredTechniques] = useState([]);
  const [displayedTechniques, setDisplayedTechniques] = useState([]);
  const [techniquesPerLoad] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [expandedTechnique, setExpandedTechnique] = useState(null);

  useEffect(() => {
    if (!user) {
      setPatreonAccess(false);
      return;
    }
    if (isAdmin) {
      setPatreonAccess(true);
      return;
    }
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        setPatreonAccess(snap.exists() && snap.data().patreonAccess === true);
      })
      .catch(() => setPatreonAccess(false));
  }, [user, isAdmin]);

  useEffect(() => {
    const loadTechniques = async () => {
      setIsLoading(true);
      try {
        const [kyohonResponse, patreonResponse] = await Promise.all([
          fetch("/kyohon.json"),
          patreonAccess ? fetch("/patreon.json") : Promise.resolve(null),
        ]);

        const kyohonData = await kyohonResponse.json();
        const kyohonCleaned = kyohonData.filter(shouldShowTutorial);

        let combined = kyohonCleaned;
        if (patreonAccess && patreonResponse) {
          const patreonData = await patreonResponse.json();
          const patreonNormalized = Array.isArray(patreonData)
            ? patreonData.map((item, index) =>
                normalizePatreonItem(item, index),
              )
            : [];
          combined = [...kyohonCleaned, ...patreonNormalized];
        }

        const shuffledData = shuffleArray(combined);
        setTechniques(shuffledData);
        setFilteredTechniques(shuffledData);
        setDisplayedTechniques(shuffledData.slice(0, techniquesPerLoad));
      } catch (error) {
        console.error("Error fetching techniques:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTechniques();
  }, [techniquesPerLoad, patreonAccess]);

  useEffect(() => {
    const savedFilters = localStorage.getItem("kyohon-filters");
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      const parsedPlatforms = Array.isArray(parsed.platforms)
        ? parsed.platforms
        : DEFAULT_FILTERS.platforms;

      const platforms =
        patreonAccess && !parsedPlatforms.includes("Patreon")
          ? [...parsedPlatforms, "Patreon"]
          : parsedPlatforms;

      setFilters({
        ...DEFAULT_FILTERS,
        ...parsed,
        platforms,
      });
    }
  }, [user, patreonAccess]);

  useEffect(() => {
    localStorage.setItem("kyohon-filters", JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    const loadUserFilters = async () => {
      if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists() && docSnap.data().platforms) {
          const storedPlatforms = Array.isArray(docSnap.data().platforms)
            ? docSnap.data().platforms
            : DEFAULT_FILTERS.platforms;
          const normalizedPlatforms =
            patreonAccess && !storedPlatforms.includes("Patreon")
              ? [...storedPlatforms, "Patreon"]
              : storedPlatforms;

          setFilters((prev) => ({
            ...prev,
            platforms: normalizedPlatforms,
          }));
        }
      }
    };
    loadUserFilters();
  }, [user, patreonAccess]);

  useEffect(() => {
    if (!patreonAccess) {
      return;
    }

    setFilters((prev) => {
      if (prev.platforms.includes("Patreon")) {
        return prev;
      }
      return {
        ...prev,
        platforms: [...prev.platforms, "Patreon"],
      };
    });
  }, [patreonAccess]);

  useEffect(() => {
    if (user) {
      const saveFiltersToFirestore = async () => {
        const docRef = doc(db, "users", user.uid);
        await setDoc(docRef, { platforms: filters.platforms }, { merge: true });
      };
      saveFiltersToFirestore();
    }
  }, [filters.platforms, user]);

  const shuffleArray = (array) => {
    let shuffledArray = [...array];
    for (let i = shuffledArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledArray[i], shuffledArray[j]] = [
        shuffledArray[j],
        shuffledArray[i],
      ];
    }
    return shuffledArray;
  };

  const presetSearches = useMemo(() => {
    const allSearches = [
      "single column tie",
      "double column tie",
      "box tie",
      "takate kote",
      "tk",
      "gote",
      "reverse prayer",
      "chest harness",
      "shinju",
      "karada",
      "hip harness",
      "futomomo",
      "tengu",
      "tenshi",
      "arm binder",

      "hogtie",
      "ebi",
      "gyaku ebi",
      "suspension",
      "side suspension",
      "face-up suspension",
      "face-down suspension",
      "uplines",
      "floorwork",

      "semenawa",
      "consent",
      "negotiation",
      "communication",
      "aftercare",
      "nerve safety",
      "frictions",
      "circulation",
    ];
    const shuffled = shuffleArray(allSearches).slice(0, 10);
    return shuffled.sort();
  }, []);

  const handleHeaderClick = () => {
    const shuffledTechniques = shuffleArray([...filteredTechniques]);
    setFilteredTechniques(shuffledTechniques);
    setDisplayedTechniques(shuffledTechniques.slice(0, techniquesPerLoad));
    setCurrentPage(1);
  };

  const handleFilterChange = useCallback((filterType, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterType]: value,
    }));
    setCurrentPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      ...DEFAULT_FILTERS,
      platforms: patreonAccess
        ? DEFAULT_FILTERS.platforms
        : DEFAULT_FILTERS.platforms.filter(
            (platform) => platform !== "Patreon",
          ),
    });
    setCurrentPage(1);
  }, [patreonAccess]);

  const matchesSearchQuery = (technique, query) => {
    const lowerCaseQuery = query.toLowerCase();
    const nameMatches = technique.name?.toLowerCase().includes(lowerCaseQuery);
    const descriptionMatches = technique.description
      ? technique.description.toLowerCase().includes(lowerCaseQuery)
      : false;
    const tagsMatches = technique.tags
      ? technique.tags.some((tag) => tag.toLowerCase().includes(lowerCaseQuery))
      : false;
    return nameMatches || descriptionMatches || tagsMatches;
  };

  const filterTechniques = useCallback(() => {
    const filtered = techniques.filter((technique) => {
      const matchesAuthor =
        filters.authors === "All" || technique.author === filters.authors;
      const matchesPlatform = filters.platforms.includes(technique.platform);
      const matchesSearchQueryField =
        filters.searchQuery === "" ||
        matchesSearchQuery(technique, filters.searchQuery);
      const matchesStreamFilter = filters.showStreams || !isStream(technique);
      return (
        matchesAuthor &&
        matchesPlatform &&
        matchesSearchQueryField &&
        matchesStreamFilter
      );
    });

    const direction = filters.sortDirection === "Ascending" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const aTime =
        Date.parse(a.date || a.publishedAt || a.scrapedAt || "") || 0;
      const bTime =
        Date.parse(b.date || b.publishedAt || b.scrapedAt || "") || 0;
      return direction * (aTime - bTime);
    });
  }, [techniques, filters]);

  useEffect(() => {
    const newFilteredTechniques = filterTechniques();
    setFilteredTechniques(newFilteredTechniques);
    setDisplayedTechniques(newFilteredTechniques.slice(0, techniquesPerLoad));
    setCurrentPage(1);
  }, [filterTechniques, techniquesPerLoad]);

  const loadMoreTechniques = useCallback(() => {
    const nextPage = currentPage + 1;
    const nextTechniques = filteredTechniques.slice(
      0,
      nextPage * techniquesPerLoad,
    );
    setDisplayedTechniques(nextTechniques);
    setCurrentPage(nextPage);
  }, [currentPage, filteredTechniques, techniquesPerLoad]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 50) {
        loadMoreTechniques();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMoreTechniques]);

  const handleExpandTechnique = (techniqueUrl) => {
    setExpandedTechnique((prev) =>
      prev === techniqueUrl ? null : techniqueUrl,
    );
  };

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setSkeletonCount(getSkeletonCount());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const uniqueValues = useMemo(() => {
    return {
      authors: ["All", ...new Set(techniques.map((t) => t.author))].sort(),
      platforms: ["All", ...new Set(techniques.map((t) => t.platform))].sort(),
    };
  }, [techniques]);

  const isFiltered = useMemo(() => {
    const allPlatforms = uniqueValues.platforms.filter(
      (platform) => platform !== "All",
    );
    const hasAllPlatformsSelected =
      filters.platforms.length === allPlatforms.length &&
      allPlatforms.every((platform) => filters.platforms.includes(platform));

    return (
      filters.authors !== "All" ||
      filters.searchQuery.trim() !== "" ||
      filters.showStreams ||
      !hasAllPlatformsSelected
    );
  }, [filters, uniqueValues.platforms]);

  const skeletonItems = useMemo(
    () => Array.from({ length: skeletonCount }),
    [skeletonCount],
  );

  return (
    <div>
      <Header
        onHeaderClick={handleHeaderClick}
        currentPath={location.pathname}
      />
      <div className="app-container">
        <div className="search-box">
          <input
            className="search-input"
            type="text"
            placeholder="Search..."
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange("searchQuery", e.target.value)}
          />
          {filters.searchQuery && (
            <div
              className="clear-search-button"
              onClick={() => handleFilterChange("searchQuery", "")}
            >
              &times;
            </div>
          )}
        </div>

        <div className="preset-searches">
          {presetSearches.map((term) => (
            <button
              key={term}
              className="preset-button"
              onClick={() => handleFilterChange("searchQuery", term)}
            >
              {term}
            </button>
          ))}
        </div>

        <div className="filter-container">
          <PlatformFilter
            platforms={uniqueValues.platforms}
            selectedPlatforms={filters.platforms}
            onChange={(updated) => handleFilterChange("platforms", updated)}
          />
          <Dropdown
            label="Author"
            options={uniqueValues.authors}
            selected={filters.authors}
            onChange={(e) => handleFilterChange("authors", e.target.value)}
          />
          <Dropdown
            label="Order"
            options={["Date Descending", "Date Ascending"]}
            selected={
              filters.sortDirection === "Ascending"
                ? "Date Ascending"
                : "Date Descending"
            }
            onChange={(e) => {
              const direction =
                e.target.value === "Date Ascending"
                  ? "Ascending"
                  : "Descending";
              handleFilterChange("sortDirection", direction);
            }}
          />
          <label
            className={`toggle-button include-streams-toggle ${filters.showStreams ? "checked" : ""}`}
            style={{marginLeft: 8}}
          >
            <input
              type="checkbox"
              checked={filters.showStreams}
              onChange={() =>
                handleFilterChange("showStreams", !filters.showStreams)
              }
            />
            Include Streams
          </label>
        </div>

        <div className="count" aria-live="polite">
          {isLoading
            ? "Loading results..."
            : `${techniques.length} results${isFiltered ? ` • Filtered to ${filteredTechniques.length}` : ""}`}
        </div>

        <div className="technique-list">
          {isLoading ? (
            skeletonItems.map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="technique-item technique-item-skeleton"
                aria-hidden="true"
              >
                <div className="skeleton-line skeleton-title" />
                <div className="skeleton-line skeleton-author" />
                <div className="skeleton-block skeleton-media" />
                <div className="skeleton-line skeleton-text" />
                <div className="skeleton-line skeleton-text short" />
                <div className="skeleton-button" />
              </div>
            ))
          ) : displayedTechniques.length > 0 ? (
            displayedTechniques.map((technique) => (
              <Technique
                key={technique.url}
                technique={technique}
                isExpanded={expandedTechnique === technique.url}
                onExpand={handleExpandTechnique}
              />
            ))
          ) : (
            <div className="empty-state-block">
              <p>No techniques match these filters.</p>
              <p className="empty-state-hint">
                Try clearing your search, selecting more platforms, or resetting
                filters.
              </p>
              <button
                className="empty-state-action"
                onClick={handleResetFilters}
              >
                Reset filters
              </button>
            </div>
          )}
        </div>

      </div>
      {showScrollButton && <ScrollToTopButton />}
      <Footer />
    </div>
  );
}

export default Kyohon;
